import { useRef } from 'react'

import { Box, Form, Tag, useUnsafeMemo } from '@factorialco/deprecated-design-system'
import { FormikProps, FormikProvider } from 'formik'
import {
  ComplianceExtraFieldDefaultSettingFragment,
  ContactFragment,
  FinanceFinancialDocumentComplianceExtraFieldsConfig,
  LastUsedSalesInvoiceSequencesDocument,
  LegalEntityForSelectFragment,
  SalesDocumentTemplateForSelectFragment,
  SerialSequenceFragment,
  TaxRateForSelectFragment,
} from 'generated/resources/graphql'
import { debounce } from 'lodash'
import moment from 'moment'

import DayPicker from 'modules/core/components/DayPicker'
import { SerialSequenceSelector } from 'modules/core/components/EntitySelectWrapper/Entities/SerialSequences'
import { LegalEntitySelector } from 'modules/core/components/LegalEntitySelector'
import NumberField from 'modules/core/components/NumberField'
import TextField from 'modules/core/components/TextField'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import { dateBackendToUI, dateUIToBackend } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { useExchangeRate } from 'modules/expenses/hooks/useExchangeRate'
import { FinanceContactsSelector } from 'modules/finance/components/Contacts/FinanceContactsSelector'
import CurrencySelector from 'modules/finance/components/CurrencySelector'
import DocumentViewer from 'modules/finance/components/DocumentViewer'
import {
  buildFormComplianceExtraFieldsWithDefaultValues,
  countryComplianceExtraFieldsConfig,
  findDefaultComplianceExtraFieldsSettingsForLegalEntity,
} from 'modules/finance/components/Invoices/SaleInvoicesUpsertSidepanel/lib/complianceExtraFields'
import { FinanceSalesDocumentTemplateSelector } from 'modules/finance/components/SalesDocumentTemplates/FinanceSalesDocumentTemplateSelector'

import { ComplianceExtraFieldsSection } from './_ComplianceExtraFieldsSection'
import { CustomizeSection } from './_CustomizeSection'
import { DiscountInput } from './_DiscountInput'
import { LineItemsSectionWithModal } from './_LineItemsSectionWithModal'
import { ProjectSection } from './_ProjectSection'
import { RetentionSection } from './_RetentionSection'
import { SectionTitle } from './_SectionTitle'
import { LineItem, SaleInvoiceFormFields } from './types'

export interface Invoice {
  id: number
}
export interface SaleInvoiceFormProps {
  form: FormikProps<SaleInvoiceFormFields>
  previewHtml?: string
  readOnly?: boolean
  defaultTaxRate?: TaxRateForSelectFragment
  disableContact?: boolean
  disableRetentionTaxRate?: boolean
  projectsModuleEnabled?: boolean
  disableProject?: boolean
  complianceExtraFieldsConfigs?: FinanceFinancialDocumentComplianceExtraFieldsConfig[]
  previousInvoiceNumber?: string | null
  excludeLegalEntityIds?: number[]
  salesDocumentTemplatesEnabled?: boolean
  defaultComplianceExtraFieldsSettings?: ComplianceExtraFieldDefaultSettingFragment[]
}

const isTaxRateValidForCountry = (
  taxRate?: TaxRateForSelectFragment | null,
  country?: string | null
) => {
  const taxRateCountry = taxRate?.taxType?.countryCode
  if (!taxRateCountry || !country || taxRateCountry == country) {
    return true
  }
}

export const adjustLineItemsTaxRateBasedOnCountry = (
  lineItems: LineItem[],
  country?: string | null
) => {
  return lineItems.map((item) => {
    return {
      ...item,
      taxRate: isTaxRateValidForCountry(item.taxRate, country) ? item.taxRate : undefined,
    }
  })
}

export const SaleInvoiceForm = ({
  form,
  previewHtml,
  readOnly = false,
  defaultTaxRate,
  disableContact = false,
  disableRetentionTaxRate = false,
  projectsModuleEnabled = false,
  disableProject = false,
  complianceExtraFieldsConfigs = [],
  previousInvoiceNumber = null,
  excludeLegalEntityIds = [],
  salesDocumentTemplatesEnabled = false,
  defaultComplianceExtraFieldsSettings = [],
}: SaleInvoiceFormProps) => {
  const { access, company } = useCurrentSession()
  const locale = access.locale

  const [fetchLastUsedSalesInvoiceSequences] = useLazyQuery(LastUsedSalesInvoiceSequencesDocument)

  const getLastUsedSequence = useUnsafeCallback(
    (legalEntityId: number) => {
      fetchLastUsedSalesInvoiceSequences({
        variables: {
          legalEntityId,
        },
      }).then(({ data }) => {
        const serialSequence = data?.finance?.salesInvoiceSequencesConnection.nodes[0]
        if (!!serialSequence) form.setFieldValue('salesInvoiceSequence', serialSequence)
      })
    },
    [fetchLastUsedSalesInvoiceSequences, form]
  )

  const debouncedGetLastUsedSequence = useUnsafeMemo(
    () => debounce((legalEntityId: number) => getLastUsedSequence(legalEntityId), 300),
    [getLastUsedSequence]
  )

  useUnsafeEffect(() => {
    if (!readOnly && form.values.legalEntity && !form.values.salesInvoiceSequence)
      debouncedGetLastUsedSequence(form.values.legalEntity?.id)
    return () => debouncedGetLastUsedSequence.cancel()
  }, [form.values.salesDocumentTemplate, debouncedGetLastUsedSequence])

  const minOperationDate = moment().subtract(180, 'days')
  const maxOperationDate = moment()

  const extractDateValue = (date: string | undefined) =>
    date !== undefined && date !== '' ? dateBackendToUI(date) : ''

  const setNewLegalEntity = (legalEntity: LegalEntityForSelectFragment | null) => {
    const previousCountry = form.values.legalEntity?.country
    const newDefaultComplianceExtraFieldsSettingsForLegalEntity = legalEntity?.id
      ? findDefaultComplianceExtraFieldsSettingsForLegalEntity(
          defaultComplianceExtraFieldsSettings,
          legalEntity.id
        )
      : undefined
    const needToResetComplianceExtraFields =
      previousCountry !== legalEntity?.country ||
      newDefaultComplianceExtraFieldsSettingsForLegalEntity
    const differentCurrency = legalEntity?.currency !== form.values.legalEntity?.currency

    form.setValues({
      ...form.values,
      legalEntity: legalEntity,
      currency: form.values.currency || legalEntity?.currency,
      // reset retention rate only if it was country specific and now it is different
      retentionTaxRate: isTaxRateValidForCountry(form.values.retentionTaxRate, legalEntity?.country)
        ? form.values.retentionTaxRate
        : undefined,
      lineItems: adjustLineItemsTaxRateBasedOnCountry(form.values.lineItems, legalEntity?.country),
      salesInvoiceSequence: undefined,
      complianceExtraFields: needToResetComplianceExtraFields
        ? buildFormComplianceExtraFieldsWithDefaultValues(
            complianceExtraFieldsConfigs,
            isCreditNoteForm,
            legalEntity?.country,
            newDefaultComplianceExtraFieldsSettingsForLegalEntity
          )
        : form.values.complianceExtraFields,
      salesDocumentTemplate: undefined,
      exchangeRate: differentCurrency ? null : form.values.exchangeRate,
    })

    if (differentCurrency) {
      updateExchangeRate(legalEntity?.currency, form.values.currency, form.values.operationDate)
    }

    if (legalEntity) {
      getLastUsedSequence(legalEntity.id)
    }
  }

  const setNewCurrency = (currency: string) => {
    form.setValues({ ...form.values, currency: currency })
    updateExchangeRate(form.values.legalEntity?.currency, currency, form.values.operationDate)
  }

  const setNewOperationDate = (operationDate: string) => {
    form.setValues({ ...form.values, operationDate: operationDate })
    updateExchangeRate(form.values.legalEntity?.currency, form.values.currency, operationDate)
  }

  const handleSalesDocumentTemplateSelect = (
    salesDocumentTemplate: SalesDocumentTemplateForSelectFragment | null
  ) => {
    form.setValues({
      ...form.values,
      salesDocumentTemplate: salesDocumentTemplate,
      showFooter: !!salesDocumentTemplate && salesDocumentTemplate?.footer !== '',
      footer: salesDocumentTemplate?.footer ?? undefined,
    })
  }

  const handleSerialSequenceSelect = (salesInvoiceSequence: SerialSequenceFragment | null) => {
    form.setValues({ ...form.values, salesInvoiceSequence: salesInvoiceSequence ?? undefined })
  }

  const handleContactSelect = (contact: ContactFragment | null) => {
    form.setValues({ ...form.values, contact: contact ?? undefined })
  }

  const isCreditNoteForm = form.values.documentType === 'credit_note'
  const selectedLegalEntityComplianceExtraFieldConfig = useUnsafeMemo(() => {
    return countryComplianceExtraFieldsConfig(
      complianceExtraFieldsConfigs,
      form.values.legalEntity?.country
    )
  }, [complianceExtraFieldsConfigs, form.values.legalEntity?.country])

  const showExchangeRate = form.values.legalEntity?.currency != form.values.currency

  const { fetch: fetchExchangeRate, loading: loadingExchangeRate } = useExchangeRate({
    onCompleted: ({ rate }) => {
      form.setValues({ ...form.values, exchangeRate: rate })
    },
    onError: () => {
      form.setValues({ ...form.values, exchangeRate: null })
      form.setErrors({
        exchangeRate: i18n.t('finance.sale_invoices.form.exchange_rate.error'),
      })
    },
  })

  // Use ref to store fetchExchangeRate and have an stable reference
  const fetchExchangeRateRef = useRef(fetchExchangeRate)
  fetchExchangeRateRef.current = fetchExchangeRate

  const updateExchangeRate = (
    legalEntityCurrency: string | undefined,
    currency: string | undefined,
    operationDate: string | undefined
  ) => {
    if (readOnly) return
    if (!currency || !legalEntityCurrency) return

    if (currency === legalEntityCurrency) return

    const dateToFetch =
      operationDate && operationDate <= moment().toISOString()
        ? operationDate
        : moment().toISOString()
    fetchExchangeRateRef.current({
      baseCurrency: currency,
      targetCurrency: legalEntityCurrency,
      date: dateToFetch,
    })
  }

  return (
    <Box aria-label="saleInvoiceForm" height="full" overflowY="hidden">
      <Box flexDirection="row" height="full">
        <Box
          width="full"
          paddingBottom="s0"
          paddingTop="s0"
          overflowY="hidden"
          height="full"
          style={{
            background: 'linear-gradient(135deg, #FAF1F1 0%, #EFE7EC 50%, #F3C4C9 100%)',
          }}
        >
          <Box paddingX="s32" position="absolute" paddingTop="s32">
            <Tag type="radical">{i18n.t('finance.sale_invoices.form.preview')}</Tag>
          </Box>
          {previewHtml && (
            <Box height="full">
              <DocumentViewer htmlContent={previewHtml} />
            </Box>
          )}
        </Box>
        <Box
          width="full"
          gap="s24"
          paddingX="s24"
          paddingTop="s24"
          paddingBottom="s24"
          flexDirection="column"
          overflowY="auto"
          height="full"
        >
          <FormikProvider value={form}>
            <Form padding="s0" notRounded>
              <Box paddingTop="s16" paddingBottom="s16" gap="s16">
                <SectionTitle
                  title={i18n.t('finance.sale_invoices.form.title')}
                  description={i18n.t('finance.sale_invoices.form.description')}
                />
                {salesDocumentTemplatesEnabled && (
                  <Box
                    width="full"
                    flexDirection="row"
                    gap="s16"
                    style={{
                      width: '50%',
                      paddingRight: '8px',
                    }}
                  >
                    <FinanceSalesDocumentTemplateSelector
                      label={i18n.t('finance.sale_invoices.form.template.title')}
                      emptyLabel={i18n.t('finance.sale_invoices.form.template.select_a_template')}
                      value={form.values.salesDocumentTemplate ?? null}
                      onSelect={handleSalesDocumentTemplateSelect}
                      disabled={readOnly}
                      queryParams={{
                        legalEntityIds: form.values.legalEntity?.id
                          ? [form.values.legalEntity.id]
                          : undefined,
                      }}
                      disableCreate
                      hideSearchIcon
                    />
                  </Box>
                )}
                <Box
                  flexDirection="row"
                  gap="s16"
                  style={{
                    width: !!previousInvoiceNumber ? '100%' : '50%',
                    paddingRight: !!previousInvoiceNumber ? '0' : '8px',
                  }}
                >
                  {!!previousInvoiceNumber && (
                    <TextField
                      readonly
                      label={i18n.t('finance.sale_invoices.form.previous_invoice')}
                      value={previousInvoiceNumber}
                      placeholder={previousInvoiceNumber}
                    />
                  )}
                  <SerialSequenceSelector
                    mandatory
                    disabled={readOnly}
                    value={form.values.salesInvoiceSequence}
                    onSelect={handleSerialSequenceSelect}
                    label={i18n.t('finance.sale_invoices.form.serial_sequence')}
                    type="sales_invoice"
                    queryParams={{
                      status: 'active',
                      legalEntityId: form.values.legalEntity?.id,
                    }}
                    error={form.errors.salesInvoiceSequence}
                    hideSearchIcon
                  />
                </Box>

                <Box flexDirection="row" gap="s16">
                  <DayPicker
                    mandatory
                    readonly={readOnly}
                    label={i18n.t('finance.sale_invoices.form.operation_date')}
                    placeholder={i18n.t('common.select')}
                    value={extractDateValue(form.values.operationDate)}
                    onChange={(value) => setNewOperationDate(dateUIToBackend(value))}
                    error={form.errors.operationDate}
                    minDate={minOperationDate}
                    maxDate={form.values.dueDate ? moment(form.values.dueDate) : maxOperationDate}
                  />
                  <DayPicker
                    mandatory
                    readonly={readOnly}
                    label={i18n.t('finance.sale_invoices.form.due_date')}
                    value={extractDateValue(form.values.dueDate)}
                    onChange={(value) =>
                      form.setValues({ ...form.values, dueDate: dateUIToBackend(value) })
                    }
                    placeholder={i18n.t('common.select')}
                    error={form.errors.dueDate}
                    minDate={
                      form.values.operationDate
                        ? moment(form.values.operationDate)
                        : minOperationDate
                    }
                  />
                </Box>

                <Box width="full" flexDirection="row" gap="s16">
                  <LegalEntitySelector
                    mandatory
                    label={i18n.t('finance.sale_invoices.form.from_to')}
                    width="full"
                    value={form.values.legalEntity ?? null}
                    onChange={setNewLegalEntity}
                    disabled={readOnly || isCreditNoteForm}
                    error={form.errors.legalEntity}
                    hideSearchIcon
                    excludeIds={excludeLegalEntityIds}
                  />
                  <FinanceContactsSelector
                    mandatory
                    disabled={readOnly || disableContact || isCreditNoteForm}
                    value={form.values.contact}
                    onSelect={handleContactSelect}
                    label={i18n.t('finance.sale_invoices.form.bill_to')}
                    type="finance_contact"
                    queryParams={{
                      companyId: company.id,
                      contactCreateType: 'client',
                    }}
                    error={form.errors.contact}
                    hideSearchIcon
                    validateOnCreate
                  />
                </Box>

                {/* If projectsModuleEnabled = false, we could offer Projects Upsell */}
                {projectsModuleEnabled && (
                  <ProjectSection
                    form={form}
                    readOnlyProject={readOnly || disableProject || isCreditNoteForm}
                    readOnlySubproject={readOnly || isCreditNoteForm}
                  />
                )}

                <Box
                  width="full"
                  flexDirection="row"
                  gap="s16"
                  style={{
                    width: showExchangeRate ? '100%' : '50%',
                    paddingRight: showExchangeRate ? '0' : '8px',
                  }}
                >
                  <CurrencySelector
                    mandatory
                    value={form.values.currency ?? ''}
                    error={form.errors.currency}
                    onChange={(value) => setNewCurrency(value)}
                    isDisabled={readOnly || isCreditNoteForm}
                  />
                  {showExchangeRate && (
                    <NumberField
                      mandatory
                      maxDecimals={8}
                      step={0.01}
                      min={0}
                      value={form.values.exchangeRate}
                      placeholder=""
                      label={i18n.t('finance.sale_invoices.form.exchange_rate.label')}
                      secondaryLabel={i18n.t('finance.sale_invoices.form.exchange_rate.info', {
                        original_currency: form.values.currency,
                        currency: form.values.legalEntity?.currency,
                      })}
                      onChange={(exchangeRate) =>
                        form.setValues({ ...form.values, exchangeRate: exchangeRate })
                      }
                      error={form.errors.exchangeRate}
                      loading={loadingExchangeRate}
                      readonly={readOnly || loadingExchangeRate}
                    />
                  )}
                </Box>
              </Box>

              <LineItemsSectionWithModal
                form={form}
                isCreditNote={isCreditNoteForm}
                readOnly={readOnly}
                defaultTaxRate={defaultTaxRate}
                complianceExtraFieldsConfig={selectedLegalEntityComplianceExtraFieldConfig}
                locale={locale}
              />

              <Box paddingTop="s16" paddingBottom="s16" gap="s16">
                <SectionTitle title={i18n.t('finance.sale_invoices.form.discount_title')} />

                <Box
                  width="full"
                  flexDirection="row"
                  gap="s24"
                  style={{
                    width: '50%',
                    paddingRight: '8px',
                  }}
                >
                  <DiscountInput form={form} readOnly={readOnly} />
                </Box>
              </Box>

              <RetentionSection
                form={form}
                readOnly={readOnly}
                disableRetentionTaxRate={disableRetentionTaxRate}
              />

              <ComplianceExtraFieldsSection
                form={form}
                isCreditNote={isCreditNoteForm}
                readOnly={readOnly}
                complianceExtraFieldsConfigs={complianceExtraFieldsConfigs}
              />

              <Box paddingTop="s16" paddingBottom="s16">
                <CustomizeSection
                  form={form}
                  readOnly={readOnly}
                  description={i18n.t('finance.sale_invoices.form.customize_description')}
                />
              </Box>
            </Form>
          </FormikProvider>
        </Box>
      </Box>
    </Box>
  )
}
